import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import Title from 'antd/lib/typography/Title';
import { FormattedMessage } from 'react-intl';
import {
  Button, Table, Tabs, Tag,
} from 'antd';
import Column from 'antd/lib/table/Column';
import VehicleDiagram from '../../../../components/diagram/VehicleDiagram';
import { biFilter } from '../utility/inspection-util';
import useIsMobileSize from '../../../../utility/hooks/useIsMobileSize';
import { formatMoney } from '../../../../utility/via-util';
import { GalleryModal} from './InspectionGallery';
import { asViaUrl, createPathWithParams, SESSION_ID_PARAM_KEY } from '../../../../utility/via-fetch';
import { getSessionCookie } from '../../../../utility/cookie-utility';


/**
 * Displays the damages for the inspection in a table.
 * @param {object[]} props.damages An array of damages to be displayed.
 */
function DamagesTable({
  existingDamages, newDamages, selectedDamage, onDamageSelect, onDataSourceChange, showGallery, filePath
}) {
  const isMobile = useIsMobileSize();
  const damages = [...existingDamages, ...newDamages];

  const createUrl = (info, damage) => createPathWithParams(`${filePath}/${info.fileId}`, {
    [SESSION_ID_PARAM_KEY]: getSessionCookie(),
    'damage-id': damage.damageId,
  });
  return (
    <Table
      onChange={(page, filters, sorter, extra) => { onDataSourceChange(extra.currentDataSource); }}
      dataSource={damages}
      pagination={false}
      size="middle"
      rowClassName={damage => (damage === selectedDamage ? 'row-highlight' : '')}
      rowKey={damage => damage.damageId}
    >
      <Column
        title={<FormattedMessage id="status" />}
        align="center"
        key="damage"
        render={(damage) => {
          const isExisting = !newDamages.includes(damage);
          return <Tag color={isExisting ? 'gold' : 'red'}>{isExisting ? <FormattedMessage id="existingDamage" /> : <FormattedMessage id="new" />}</Tag>;
        }}
        filters={[{ text: <FormattedMessage id="existingDamage" />, value: 'existing' }, { text: <FormattedMessage id="new" />, value: 'new' }]}
        onFilter={(filterValue, damage) => {
          if (filterValue === 'existing') return existingDamages.includes(damage);
          if (filterValue === 'new') return newDamages.includes(damage);
          return false;
        }}
      />
      <Column title={<FormattedMessage id="area" />} dataIndex="damageArea.damageAreaDescription" key="damageArea.damageAreaCode" />
      <Column title={<FormattedMessage id="type" />} dataIndex="damageType.damageTypeDescription" key="damageType.damageTypeCode" />
      <Column className={isMobile && 'col-hide'} title={<FormattedMessage id="severity" />} dataIndex="damageSeverity.damageSeverityDescription" key="damageSeverity.damageSeverityCode" />
      <Column
        title={<FormattedMessage id="estimatedCost" />}
        align="right"
        dataIndex="repairCost"
        key="damageId"
        render={(text, damage) => {
          const isExisting = !newDamages.includes(damage);
          if (isExisting || !damage.repairCost) return <FormattedMessage id="noData" />;
          return formatMoney(damage.repairCost, damage.currencyCode);
        }}
      />
      <Column
        align="right"
        width="110px"
        render={(damage) => {
          if (!damage.fileInfo || damage.fileInfo.length === 0) return <></>; // No media linked to the damage
          var count = 0;
          const fileList = damage.fileInfo.map(info => ({
            index: count++,
            uid: info.fileId,
            name: info.filename,
            status: 'done',
            url: createUrl(info, damage),
            contentType: info.contentType,
          }));
          count = 0;
          return (
            <Button
              onClick={() => showGallery(fileList)}
              size="small"
              type="default"
              // icon="file-image"
              style={{background: "var(--via-icon-images)", border:"none", backgroundSize: "contain", height:"24px", width:"24px", paddingLeft: "4px"}}
            />
          );
        }}
      />
      <Column
        align="right"
        width="110px"
        render={(damage) => {
          if (!damage.damageYCoordinate || isMobile) return <></>; // The damage is in the wrong list!

          const isSelected = damage === selectedDamage;
          return (
            <Button
              onClick={() => onDamageSelect(!isSelected ? damage : null)}
              size="small"
              type={!isSelected ? 'default' : 'danger'}
              icon={!isSelected ? 'environment' : 'close'}
            >
              <FormattedMessage id={!isSelected ? 'zoomIn' : 'cancel'} />
            </Button>
          );
        }}
      />
    </Table>
  );
}

DamagesTable.propTypes = {
  existingDamages: PropTypes.arrayOf(PropTypes.shape({})),
  newDamages: PropTypes.arrayOf(PropTypes.shape({})),
  selectedDamage: PropTypes.shape({}),
  onDamageSelect: PropTypes.func,
  onDataSourceChange: PropTypes.func,
};

DamagesTable.defaultProps = {
  existingDamages: [],
  newDamages: [],
  selectedDamage: null,
  onDamageSelect: () => {},
  onDataSourceChange: () => {},
};


/**
 * Shows all the damages that were marked on the diagram.
 * @param {string} props.vehicleDiagramCode The identifier of the vehicle diagram.
 * @param {array} props.newVisibleDamages An array of damages that are new at the time of recording the inspection.
 * @param {array} props.existingVisibleDamages An array of damages that were existing before the inspection was recorded.
 */
function InspectionDiagramDamages({
  vehicleDiagramCode, newVisibleDamages, existingVisibleDamages, externalCustomer, filePath
}) {
  const [selectedDamage, setSelectedDamage] = useState();

  const [visibleDamages, setVisibleDamages] = useState([]);
  useEffect(() => {
    setVisibleDamages(newVisibleDamages.concat(existingVisibleDamages));
  }, [newVisibleDamages, existingVisibleDamages]);

  useEffect(() => {
    // If the selected daamge is no longer visible then we need to unselect it.
    if (selectedDamage && !visibleDamages.includes(selectedDamage)) {
      setSelectedDamage();
    }
  }, [selectedDamage, visibleDamages]);

const [preview, setPreview] = useState({ visible: false, file: undefined, fileList: [] });

const switchFile = newIndex => setPreview({...preview, file: preview.fileList[newIndex]});
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={() => { setSelectedDamage(); }}
      className="inspection-diagram-damages"
    >
      <div
        role="presentation"
        aria-hidden="true" // The diagram has no meaning to a screen reader.
        onClick={() => { setSelectedDamage(); }}
      >
        <VehicleDiagram
          diagramCode={vehicleDiagramCode}
          newDamages={visibleDamages.filter(damage => newVisibleDamages.includes(damage))}
          existingDamages={visibleDamages.filter(damage => existingVisibleDamages.includes(damage))}
          selectedArea={selectedDamage ? selectedDamage.damageArea : null}
          onDamageSelect={(damageId) => { setSelectedDamage(visibleDamages.find(el => damageId === el.damageId)); }}
          className="damages-diagram"
          external={externalCustomer}
        />
      </div>
      <div>
        <DamagesTable
          newDamages={newVisibleDamages}
          existingDamages={existingVisibleDamages}
          onDamageSelect={setSelectedDamage}
          selectedDamage={selectedDamage}
          onDataSourceChange={setVisibleDamages}
          showGallery={fileInfo => setPreview({visible: true, file: fileInfo[0], fileList: fileInfo })}
          filePath={filePath}
        />
        <GalleryModal switchFile={switchFile} visible={preview.visible} file={preview.file} fileList={preview.fileList} onCancel={() => { setPreview({ visible: false, src: undefined, fileList: []}); }} />
      </div>
    </div>
  );
}

InspectionDiagramDamages.propTypes = {
  vehicleDiagramCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  newVisibleDamages: PropTypes.arrayOf(PropTypes.shape({})),
  existingVisibleDamages: PropTypes.arrayOf(PropTypes.shape({})),
  externalCustomer: PropTypes.bool,
};

InspectionDiagramDamages.defaultProps = {
  newVisibleDamages: [],
  existingVisibleDamages: [],
  externalCustomer: false,
};


/**
 * Shows all the damages that were not marked on a diagram.
 * @param {array} props.newAdhocDamages An array of damages that are new at the time of recording the inspection.
 * @param {array} props.existingAdhocDamages An array of damages that were existing before the inspection was recorded.
 */
function InspectionAdhocDamages({ newAdhocDamages, existingAdhocDamages, filePath }) {
  const [preview, setPreview] = useState({ visible: false, file: undefined, fileList: [] });
  const switchFile = newIndex => setPreview({...preview, file: preview.fileList[newIndex]});
  return (
    <div>
      <DamagesTable
        newDamages={newAdhocDamages}
        existingDamages={existingAdhocDamages}
        filePath={filePath}
        showGallery={fileInfo => setPreview({visible: true, file: fileInfo[0], fileList: fileInfo })}
      />
      <GalleryModal switchFile={switchFile} visible={preview.visible} file={preview.file} fileList={preview.fileList} onCancel={() => { setPreview({ visible: false, src: undefined, fileList: []}); }} />
    </div>
  );
}

InspectionAdhocDamages.propTypes = {
  newAdhocDamages: PropTypes.arrayOf(PropTypes.shape({})),
  existingAdhocDamages: PropTypes.arrayOf(PropTypes.shape({})),
};

InspectionAdhocDamages.defaultProps = {
  newAdhocDamages: [],
  existingAdhocDamages: [],
};


/**
 * A component for displaying the damages in an inspection.
 * @param {object} props.inspection The inspection with damages to be displayed.
 */
export default function InspectionDamages({
  vehicleDiagramCode, newDamages, existingDamages, externalCustomer, inspectionCode, externalKey
}) {
  const [newVisibleDamages, newAdhocDamages] = biFilter(newDamages, damage => damage.damageXCoordinate && damage.damageYCoordinate);
  const [existingVisibleDamages, existingAdhocDamages] = biFilter(existingDamages, damage => damage.damageXCoordinate && damage.damageYCoordinate);

  const loggedInPath = asViaUrl(`/api/inspections/${inspectionCode}/files`);
  const externalPath = asViaUrl(`/api/inspections/external/${externalKey}/files`);
  
  var filePath = externalCustomer ? externalPath : loggedInPath;
  return (
    <div>
      <Title level={3} style={{ marginBottom: '0.3em' }}><FormattedMessage id="damages" /></Title>
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane tab={<FormattedMessage id="diagramDamages" />} key="1">
          <InspectionDiagramDamages
            vehicleDiagramCode={vehicleDiagramCode}
            newVisibleDamages={newVisibleDamages}
            existingVisibleDamages={existingVisibleDamages}
            externalCustomer={externalCustomer}
            filePath={filePath}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={<FormattedMessage id="additionalDamages" />} key="2">
          <InspectionAdhocDamages
            newAdhocDamages={newAdhocDamages}
            existingAdhocDamages={existingAdhocDamages}
            filePath={filePath}
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}

InspectionDamages.propTypes = {
  vehicleDiagramCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  newDamages: PropTypes.arrayOf(PropTypes.shape({})),
  existingDamages: PropTypes.arrayOf(PropTypes.shape({})),
  externalCustomer: PropTypes.bool,
};

InspectionDamages.defaultProps = {
  newDamages: [],
  existingDamages: [],
  externalCustomer: false,
};
